import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Confirm,
  Container,
  Dimmer,
  Loader,
  Pagination,
  Segment,
  Table,
} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import {
  cancelRecurringPayment,
  closeCancelRecurringPaymentConfirmation,
  getRecurringPaymentHistoryAction,
  showCancelRecurringPaymentConfirmation,
} from 'state/business/actions';

const PAGE_SIZE = 10;

const mapStateToProps = ({ business }) => ({
  recurringPaymentHistoryData: business.recurringPaymentHistoryData,
  isFetching: business.gettingBusinessDetails,
  allBusiness: business.allBusiness,
  cancelRecurringPaymentConfirmation:
    business.cancelRecurringPaymentConfirmation,
  cancellingRecurringPayment: business.cancellingRecurringPayment,
});

const getStatusColor = status => {
  switch (status) {
    case 'BINDING':
      return 'yellow';
    case 'PROCESSING':
      return 'blue';
    case 'FAILED_BINDING':
      return 'red';
    case 'ACTIVE':
      return 'green';
    case 'CANCELLING':
      return 'orange';
    case 'CANCELLED':
      return 'red';
    default:
      return 'black';
  }
};

const convertDate = payloadDate => {
  if (payloadDate === null) return '-';
  const newPayloadDate = payloadDate.substr(0, payloadDate.indexOf('Z') + 1);
  const dateTimeFormat = new Intl.DateTimeFormat('id', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
  const date = new Date(newPayloadDate);
  return dateTimeFormat.format(date);
};

const RecurringPaymentHistory = connect(mapStateToProps)(props => {
  const {
    dispatch,
    allBusiness,
    recurringPaymentHistoryData,
    isFetching,
    cancelRecurringPaymentConfirmation,
    cancellingRecurringPayment,
  } = props;
  const businessId = allBusiness.data[allBusiness.activeIndex].id;

  let paging = null;
  if (recurringPaymentHistoryData) {
    paging = recurringPaymentHistoryData.paging;
  }
  let totalPages = 0;
  if (paging) totalPages = Math.ceil(paging.total / paging.size);

  const fetchFirstPage = useCallback(() => {
    const payload = {
      businessId,
      page: 1,
      size: PAGE_SIZE,
    };

    dispatch(getRecurringPaymentHistoryAction(payload));
  }, [businessId, dispatch]);

  const handleClickCancelRecurringPayment = () => {
    dispatch(showCancelRecurringPaymentConfirmation());
  };

  const handleConfirmCancelRecurringPayment = () => {
    dispatch(closeCancelRecurringPaymentConfirmation());
    dispatch(cancelRecurringPayment(businessId, fetchFirstPage));
  };

  const handleOnPageChange = (_, val) => {
    const { activePage } = val;
    const payload = {
      businessId,
      page: activePage,
      size: PAGE_SIZE,
    };
    dispatch(getRecurringPaymentHistoryAction(payload));
  };

  useEffect(() => {
    fetchFirstPage();
  }, [businessId, dispatch, fetchFirstPage]);

  return (
    <Container
      style={{ overflowY: 'scroll', maxHeight: '50vh', padding: '1vh' }}
    >
      <Confirm
        open={cancelRecurringPaymentConfirmation}
        confirmButton={{ negative: true, content: 'Confirm' }}
        onConfirm={handleConfirmCancelRecurringPayment}
        onCancel={() => dispatch(closeCancelRecurringPaymentConfirmation())}
        header="Confirm Cancel Recurring Payment"
        content="Are you sure to perform this operation? Any progress cannot be rolled back"
      />
      <Segment style={{ padding: '0px' }}>
        <Dimmer active={isFetching || cancellingRecurringPayment}>
          <Loader size="massive">Loading</Loader>
        </Dimmer>
        <Table celled selectable style={{ margin: '0px' }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Date</Table.HeaderCell>
              <Table.HeaderCell>RPI Code</Table.HeaderCell>
              <Table.HeaderCell>Customer ID</Table.HeaderCell>
              <Table.HeaderCell>Month</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {recurringPaymentHistoryData.data &&
            recurringPaymentHistoryData.data.length > 0 ? (
              recurringPaymentHistoryData.data.map(history => {
                return (
                  <Table.Row key={history.rpi_code}>
                    <Table.Cell>{convertDate(history.created_at)}</Table.Cell>
                    <Table.Cell>{history.rpi_code}</Table.Cell>
                    <Table.Cell>{history.customer_id}</Table.Cell>
                    <Table.Cell>{history.month}</Table.Cell>
                    <Table.Cell
                      style={{ color: getStatusColor(history.status) }}
                    >
                      {history.status}
                      {history.status === 'ACTIVE' && (
                        <Button
                          color="red"
                          style={{ marginLeft: '8px' }}
                          onClick={handleClickCancelRecurringPayment}
                        >
                          Cancel
                        </Button>
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })
            ) : (
              <>
                <Table.Row>
                  <Table.Cell colSpan={5} textAlign="center">
                    No Recurring Payment History
                  </Table.Cell>
                </Table.Row>
              </>
            )}
          </Table.Body>

          {paging && paging.total > 0 ? (
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan="7">
                  <Pagination
                    activePage={paging.page}
                    totalPages={totalPages}
                    onPageChange={handleOnPageChange}
                    size="mini"
                  />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          ) : (
            <></>
          )}
        </Table>
      </Segment>
    </Container>
  );
});

RecurringPaymentHistory.propTypes = {
  dispatch: PropTypes.func.isRequired,
  RecurringPaymentHistoryData: PropTypes.object.isRequired,
  isFetching: PropTypes.bool.isRequired,
  allBusiness: PropTypes.object.isRequired,
  cancelRecurringPaymentConfirmation: PropTypes.bool.isRequired,
  cancellingRecurringPayment: PropTypes.bool.isRequired,
};

export default RecurringPaymentHistory;
